// ─── src/pages/ShowcaseRendererFlagship.jsx ──────────────────────────────────
// FLAGSHIP VARIANT of the showcase renderer.
//
//   Trigger:  append `?v=flagship` to any showcase URL
//             e.g. /showcase/al-habtoor-palace-budapest?v=flagship
//
// This is a self-contained, non-breaking parallel renderer. The original
// ShowcaseRenderer.jsx remains the default and is not touched by this file.
// Wired into ShowcasePage.jsx via a query-param switch.
//
// Design target: Condé Nast Traveller / Aman / Mr & Mrs Smith presentation
// level. Editorial typography, asymmetric layouts, cinematic imagery, gold
// accents. Reads the SAME sections JSONB shape as the default renderer so a
// showcase can be viewed in either variant without data changes.
//
// Key differentiators versus default:
//   - Hero has a 5-level fallback cascade and NEVER renders an empty void
//     (typographic gradient panel with etched venue title as last resort).
//   - Room layouts cycle through FOUR distinct patterns (i % 4).
//   - Gallery is a true editorial mosaic, not a uniform grid.
//   - CTA is a decision-moment split with inline enquiry form shell.
//   - All sections use the shared palette system duplicated inline.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useRef, useMemo } from 'react';
import { imgUrl } from "../utils/imgUrl";
import HomeNav              from '../components/nav/HomeNav';
import MediaBlock           from '../components/profile/MediaBlock';
import { ThemeCtx, LIGHT }  from '../components/profile/ProfileDesignSystem';
import { useBreakpoint }    from '../hooks/useWindowWidth';

const FD = 'var(--font-heading-primary)';
const FB = 'var(--font-body)';

// ── Palette system (duplicated from default renderer, self-contained) ────────
const DARK_PALETTE = {
  mode:               'dark',
  bg:                 '#0a0a08',
  bg2:                '#0f0e0c',
  bg3:                '#1a1209',
  accent:             '#d39a21',
  text:               '#f5f0e8',
  muted:              'rgba(245,240,232,0.72)',
  label:              '#d39a21',
  navBg:              'rgba(10,10,8,0.94)',
  navBorder:          'rgba(211,154,33,0.14)',
  navText:            'rgba(245,240,232,0.48)',
  navActive:          '#d39a21',
  heroOverlayOpacity: 0.45,
};

const LIGHT_DEFAULTS = {
  mode:               'light',
  bg:                 '#FDFBF7',
  bg2:                '#F5F0E8',
  bg3:                '#EDE8DC',
  accent:             '#B8962E',
  text:               '#1C1410',
  muted:              'rgba(28,20,16,0.55)',
  label:              '#B8962E',
  navBg:              'rgba(253,251,247,0.97)',
  navBorder:          'rgba(184,150,46,0.18)',
  navText:            'rgba(28,20,16,0.45)',
  navActive:          '#B8962E',
  heroOverlayOpacity: 0.38,
};

function buildPalette(themeJson) {
  if (!themeJson || themeJson.mode !== 'light') return DARK_PALETTE;
  return { ...LIGHT_DEFAULTS, ...themeJson };
}

// ── Video URL parser (supports YouTube, Vimeo, direct mp4/webm) ──────────────
function parseVideoUrl(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (yt) return { type: 'youtube', id: yt[1] };
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return { type: 'vimeo', id: vm[1] };
  if (/\.(mp4|webm|mov|ogg|m4v|avi|mkv)(\?|$)/i.test(url)) return { type: 'direct', url };
  return null;
}

// ── Image resolver used by non-hero sections ─────────────────────────────────
function resolveImage(sectionImage, showcaseHero, listingFirstImage) {
  if (Array.isArray(sectionImage)) {
    const first = sectionImage.find(i => (typeof i === 'string' ? i : i?.url));
    if (first) return typeof first === 'string' ? first : first.url;
  }
  return sectionImage || showcaseHero || listingFirstImage || null;
}

// ── Leaflet loader (shared with default, safe to redefine, same URLs) ───────
const LEAFLET_CSS_URL = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS_URL  = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
let _leafletPromise = null;
function loadLeafletOnce() {
  if (_leafletPromise) return _leafletPromise;
  _leafletPromise = new Promise(resolve => {
    if (typeof window === 'undefined') { resolve(null); return; }
    if (window.L) { resolve(window.L); return; }
    if (!document.querySelector(`link[href="${LEAFLET_CSS_URL}"]`)) {
      const l = document.createElement('link'); l.rel = 'stylesheet'; l.href = LEAFLET_CSS_URL;
      document.head.appendChild(l);
    }
    const s = document.createElement('script'); s.src = LEAFLET_JS_URL;
    s.onload = () => resolve(window.L);
    document.head.appendChild(s);
  });
  return _leafletPromise;
}

// ── Global keyframes (mounted once by root component) ────────────────────────
function FlagshipStyles() {
  return (
    <style>{`
      @keyframes flagship-kb {
        0%   { transform: scale(1.05); }
        100% { transform: scale(1.14); }
      }
      @keyframes flagship-scroll-cue {
        0%, 100% { transform: translateY(0); opacity: 0.75; }
        50%      { transform: translateY(6px); opacity: 1; }
      }
      @keyframes flagship-fade-up {
        0%   { opacity: 0; transform: translateY(24px); }
        100% { opacity: 1; transform: translateY(0); }
      }
      @keyframes flagship-gold-sweep {
        0%   { background-position: 0% 50%; }
        100% { background-position: 200% 50%; }
      }
      .flagship-gallery-tile {
        transition: transform 800ms cubic-bezier(0.19, 1, 0.22, 1);
      }
      .flagship-gallery-tile:hover img {
        transform: scale(1.06);
      }
      .flagship-gallery-tile img {
        transition: transform 1200ms cubic-bezier(0.19, 1, 0.22, 1);
      }
      .flagship-cta-input:focus {
        outline: none;
        border-color: #d39a21 !important;
        background: rgba(255,255,255,0.06) !important;
      }
      .flagship-sec-nav-link {
        position: relative;
      }
      .flagship-sec-nav-link::after {
        content: '';
        position: absolute; left: 50%; bottom: -8px;
        width: 0; height: 1px;
        background: currentColor;
        transition: all 220ms ease;
        transform: translateX(-50%);
      }
      .flagship-sec-nav-link[data-active="true"]::after {
        width: 28px;
      }
      .flagship-room-amenity {
        transition: color 220ms ease;
      }
    `}</style>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HERO, never renders empty
// ─────────────────────────────────────────────────────────────────────────────
function FlagshipHero({ content, showcaseHero, listingFirstImage, palette, venueTitle }) {
  const { isMobile } = useBreakpoint();
  const [videoFailed, setVideoFailed] = useState(false);

  // ── Image cascade (5 levels) ───────────────────────────────────────────────
  const imagesFromArray = (content.images || [])
    .map(img => (typeof img === 'string' ? img : img?.url))
    .filter(Boolean);
  const singleImg = content.image || null;

  let heroImages = [];
  if (imagesFromArray.length) heroImages = imagesFromArray;
  else if (singleImg)         heroImages = [singleImg];
  else if (showcaseHero)      heroImages = [showcaseHero];
  else if (listingFirstImage) heroImages = [listingFirstImage];

  const hasAnyImage = heroImages.length > 0;
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    if (heroImages.length <= 1) return;
    const t = setInterval(() => setImgIdx(i => (i + 1) % heroImages.length), 6000);
    return () => clearInterval(t);
  }, [heroImages.length]);

  const vid = parseVideoUrl(content.videoUrl);
  const showVideo = vid && !videoFailed;
  const ytEmbed = vid?.type === 'youtube'
    ? `https://www.youtube.com/embed/${vid.id}?autoplay=1&mute=1&loop=1&playlist=${vid.id}&controls=0&rel=0&modestbranding=1&iv_load_policy=3&cc_load_policy=0&cc_lang_pref=none&playsinline=1&enablejsapi=1&origin=${typeof window !== 'undefined' ? encodeURIComponent(window.location.origin) : ''}`
    : null;
  const vmEmbed = vid?.type === 'vimeo'
    ? `https://player.vimeo.com/video/${vid.id}?autoplay=1&muted=1&loop=1&background=1&texttrack=false`
    : null;
  const videoCredit = content.videoCredit || content.video_credit || null;
  const heroVideoRef = useRef(null);

  // Force captions OFF via the real YouTube IFrame API. A raw enablejsapi iframe
  // ignores postMessage commands until the API handshake, so unloadModule only
  // works when driven through YT.Player, this reliably removes auto-captions.
  useEffect(() => {
    if (vid?.type !== 'youtube' || !vid?.id) return;
    let cancelled = false, capTimer = null, player = null;
    const killCaps = (p) => { try { p.unloadModule('captions'); p.unloadModule('cc'); p.setOption('captions', 'track', {}); p.setOption('cc', 'track', {}); } catch {} };
    const ensureApi = () => new Promise((resolve) => {
      if (window.YT && window.YT.Player) return resolve(window.YT);
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { try { prev && prev(); } catch {} resolve(window.YT); };
      if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
        const s = document.createElement('script'); s.src = 'https://www.youtube.com/iframe_api'; document.head.appendChild(s);
      }
    });
    ensureApi().then((YT) => {
      if (cancelled || !heroVideoRef.current || !YT?.Player) return;
      try {
        player = new YT.Player(heroVideoRef.current, {
          events: {
            onReady: (e) => { try { e.target.mute(); } catch {} killCaps(e.target); if (!capTimer) capTimer = setInterval(() => killCaps(e.target), 1500); },
            onStateChange: (e) => { if (e.data === 1) killCaps(e.target); },
          },
        });
      } catch {}
    });
    return () => { cancelled = true; if (capTimer) clearInterval(capTimer); try { player?.destroy?.(); } catch {} };
  }, [vid?.type, vid?.id]);

  const title    = content.title    || venueTitle || '';
  const subtitle = content.subtitle || '';
  const tagline  = content.tagline  || '';
  const excerpt  = content.excerpt  || content.body || '';

  // Responsive title scale, clamp with longer titles shrinking further
  const titleLen = title.length;
  const dTitle = titleLen > 28 ? 88 : titleLen > 18 ? 108 : 128;
  const mTitle = titleLen > 22 ? 44 : 54;

  return (
    <section style={{
      position: 'relative',
      width: '100%',
      height: isMobile ? '90vh' : '100vh',
      minHeight: isMobile ? 580 : 720,
      overflow: 'hidden',
      background: '#050403',
    }}>
      {/* ── Layer 1: media (video > slides > typographic void) ──────────────── */}
      {showVideo ? (
        vid.type === 'direct' ? (
          <video
            src={vid.url}
            autoPlay muted loop playsInline
            onError={() => setVideoFailed(true)}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <iframe
            ref={heroVideoRef}
            src={ytEmbed || vmEmbed}
            allow="autoplay; fullscreen"
            style={{
              position: 'absolute', inset: '-4px',
              width: 'calc(100% + 8px)', height: 'calc(100% + 8px)',
              border: 'none', pointerEvents: 'none',
            }}
          />
        )
      ) : hasAnyImage ? (
        heroImages.map((src, i) => (
          <div
            key={`${src}-${i}`}
            style={{
              position: 'absolute', inset: 0,
              opacity: i === imgIdx ? 1 : 0,
              transition: 'opacity 1200ms ease',
            }}
          >
            <img
              src={imgUrl(src, { width: 1600 })}
              alt={title}
              style={{
                position: 'absolute', inset: 0,
                width: '100%', height: '100%',
                objectFit: 'cover',
                objectPosition: content.focal_point || 'center center',
                animation: 'flagship-kb 8000ms ease-out forwards',
                animationPlayState: i === imgIdx ? 'running' : 'paused',
              }}
            />
          </div>
        ))
      ) : (
        // ── Typographic fallback, never a black void ───────────────────────
        <div style={{
          position: 'absolute', inset: 0,
          background: 'radial-gradient(ellipse at center, #1a1410 0%, #0a0806 55%, #050403 100%)',
        }}>
          {/* Noise overlay */}
          <div style={{
            position: 'absolute', inset: 0,
            backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.5  0 0 0 0 0.42  0 0 0 0 0.28  0 0 0 0.18 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>")`,
            opacity: 0.35, mixBlendMode: 'overlay',
          }} />
          {/* Etched venue title (centre, large, low opacity) */}
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: isMobile ? '0 28px' : '0 8vw',
          }}>
            <div style={{
              fontFamily: FD,
              fontSize: isMobile ? 68 : 180,
              lineHeight: 0.95,
              letterSpacing: '-0.03em',
              color: 'transparent',
              WebkitTextStroke: '1px rgba(211,154,33,0.22)',
              textAlign: 'center',
              userSelect: 'none',
              fontWeight: 400,
              fontStyle: 'italic',
              textShadow: '0 0 40px rgba(0,0,0,0.5)',
            }}>
              {venueTitle || 'A Place of Singular Beauty'}
            </div>
          </div>
        </div>
      )}

      {/* ── Layer 2: cinematic gradient overlay ───────────────────────────── */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0.10) 75%, rgba(0,0,0,0.40) 100%)',
        pointerEvents: 'none',
      }} />

      {/* Videographer credit, subtle, bottom-right corner */}
      {showVideo && videoCredit && (
        <div style={{
          position: 'absolute', bottom: 16, right: 20, zIndex: 4,
          fontSize: 11, letterSpacing: '0.05em',
          color: 'rgba(245,240,232,0.72)',
          textShadow: '0 1px 3px rgba(0,0,0,0.6)',
          fontStyle: 'italic', pointerEvents: 'none', userSelect: 'none',
        }}>
          Film by {videoCredit}
        </div>
      )}

      {/* ── Layer 3: copy block, bottom-left ───────────────────────────────── */}
      <div style={{
        position: 'absolute',
        left: 0, right: 0, bottom: isMobile ? 64 : 96,
        padding: isMobile ? '0 24px' : '0 7vw',
        color: '#f5f0e8',
        animation: 'flagship-fade-up 900ms cubic-bezier(0.19, 1, 0.22, 1) both',
      }}>
        {tagline && (
          <div style={{
            fontFamily: FB,
            fontSize: 11,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: palette.accent,
            marginBottom: isMobile ? 14 : 22,
            fontWeight: 500,
          }}>
            {tagline}
          </div>
        )}
        {title && (
          <h1 style={{
            fontFamily: FD,
            fontSize: isMobile ? mTitle : dTitle,
            lineHeight: 1.0,
            letterSpacing: '-0.02em',
            fontWeight: 400,
            margin: 0,
            maxWidth: isMobile ? '100%' : '18ch',
            color: '#f5f0e8',
            fontStyle: content.titleStyle === 'italic' ? 'italic' : 'normal',
          }}>
            {title}
          </h1>
        )}
        {subtitle && (
          <div style={{
            fontFamily: FD,
            fontSize: isMobile ? 20 : 28,
            fontStyle: 'italic',
            fontWeight: 300,
            color: 'rgba(245,240,232,0.88)',
            marginTop: isMobile ? 14 : 18,
            lineHeight: 1.2,
          }}>
            {subtitle}
          </div>
        )}
        {excerpt && (
          <p style={{
            fontFamily: FB,
            fontSize: isMobile ? 15 : 17,
            lineHeight: 1.7,
            color: 'rgba(245,240,232,0.82)',
            maxWidth: 560,
            marginTop: isMobile ? 20 : 28,
            marginBottom: 0,
            fontWeight: 300,
          }}>
            {excerpt}
          </p>
        )}
      </div>

      {/* ── Layer 4: scroll cue (bottom-right) ──────────────────────────────── */}
      {!isMobile && (
        <div style={{
          position: 'absolute',
          right: '7vw', bottom: 40,
          display: 'flex', alignItems: 'center', gap: 14,
          animation: 'flagship-scroll-cue 1800ms ease-in-out infinite',
          pointerEvents: 'none',
        }}>
          <div style={{
            width: 48, height: 1,
            background: palette.accent, opacity: 0.6,
          }} />
          <div style={{
            fontFamily: FB, fontSize: 10,
            letterSpacing: '0.28em', textTransform: 'uppercase',
            color: 'rgba(245,240,232,0.7)',
          }}>Scroll</div>
          <svg width="10" height="14" viewBox="0 0 10 14" fill="none">
            <path d="M5 1v12m0 0l-4-4m4 4l4-4"
                  stroke={palette.accent} strokeWidth="1" strokeLinecap="round" />
          </svg>
        </div>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// INTRO, asymmetric editorial pull
// ─────────────────────────────────────────────────────────────────────────────
function FlagshipIntro({ content, palette }) {
  const { isMobile } = useBreakpoint();
  const label = content.label || content.eyebrow || 'A Story';
  const title = content.title || '';
  const body  = content.body  || content.text || '';
  const byline = content.byline || content.location || '';

  return (
    <section style={{
      background: palette.bg,
      color: palette.text,
      padding: isMobile ? '80px 24px' : '140px 7vw',
    }}>
      <div style={{
        maxWidth: 1280,
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '40% 60%',
        gap: isMobile ? 36 : 80,
        alignItems: 'start',
      }}>
        {/* Left column */}
        <aside>
          <div style={{
            fontFamily: FB, fontSize: 10,
            letterSpacing: '0.28em', textTransform: 'uppercase',
            color: palette.accent, fontWeight: 600,
          }}>
            {label}
          </div>
          <div style={{
            width: 40, height: 1,
            background: palette.accent,
            marginTop: 18, marginBottom: 22,
          }} />
          {byline && (
            <div style={{
              fontFamily: FB,
              fontSize: 14, lineHeight: 1.65,
              color: palette.muted,
              fontStyle: 'italic',
              maxWidth: 280,
            }}>
              {byline}
            </div>
          )}
          {title && (
            <h2 style={{
              fontFamily: FD,
              fontSize: isMobile ? 32 : 40,
              lineHeight: 1.15,
              letterSpacing: '-0.01em',
              fontWeight: 400,
              color: palette.text,
              margin: '28px 0 0',
              maxWidth: '14ch',
            }}>
              {title}
            </h2>
          )}
        </aside>

        {/* Right column */}
        <div>
          {body && (
            <div style={{
              fontFamily: FB,
              fontSize: isMobile ? 18 : 24,
              lineHeight: 1.7,
              fontWeight: 300,
              color: palette.text,
              whiteSpace: 'pre-wrap',
            }}>
              {body}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ROOMS, cycles through 4 layouts by index
// ─────────────────────────────────────────────────────────────────────────────
function FlagshipRooms({ content, palette, showcaseHero, listingFirstImage }) {
  const { isMobile } = useBreakpoint();
  const rooms = (content.rooms || []).filter(r => r && (r.title || r.name || r.description));
  if (!rooms.length) return null;

  const sectionTitle    = content.title    || 'Rooms & Suites';
  const sectionLabel    = content.label    || content.eyebrow || 'Accommodations';
  const sectionSubtitle = content.subtitle || '';

  return (
    <section style={{
      background: palette.bg,
      color: palette.text,
      padding: isMobile ? '70px 0 90px' : '120px 0 140px',
    }}>
      {/* Section header */}
      <div style={{
        padding: isMobile ? '0 24px 48px' : '0 7vw 80px',
        maxWidth: 1280, margin: '0 auto',
      }}>
        <div style={{
          fontFamily: FB, fontSize: 10,
          letterSpacing: '0.28em', textTransform: 'uppercase',
          color: palette.accent, fontWeight: 600,
        }}>
          {sectionLabel}
        </div>
        <div style={{ width: 40, height: 1, background: palette.accent, marginTop: 16, marginBottom: 22 }} />
        <h2 style={{
          fontFamily: FD,
          fontSize: isMobile ? 40 : 64,
          lineHeight: 1.0, letterSpacing: '-0.02em',
          fontWeight: 400, margin: 0, maxWidth: '16ch',
        }}>
          {sectionTitle}
        </h2>
        {sectionSubtitle && (
          <p style={{
            fontFamily: FB,
            fontSize: isMobile ? 16 : 19,
            lineHeight: 1.7, fontWeight: 300,
            color: palette.muted,
            marginTop: 24, marginBottom: 0,
            maxWidth: 640,
          }}>
            {sectionSubtitle}
          </p>
        )}
      </div>

      {rooms.map((room, i) => (
        <FlagshipRoomCard
          key={i}
          room={room}
          index={i}
          palette={palette}
          showcaseHero={showcaseHero}
          listingFirstImage={listingFirstImage}
        />
      ))}
    </section>
  );
}

function FlagshipRoomCard({ room, index, palette, showcaseHero, listingFirstImage }) {
  const { isMobile } = useBreakpoint();
  const layoutIdx = index % 4;
  const images = (room.images || []).filter(img => img && (typeof img === 'string' ? img : img.url));
  const resolvedImages = images.map(img => typeof img === 'string' ? img : img.url);
  const primary   = resolvedImages[0] || showcaseHero || listingFirstImage || null;
  const secondary = resolvedImages[1] || primary;
  const tertiary  = resolvedImages[2] || secondary;

  const title       = room.title || room.name || 'Suite';
  const description = room.description || room.body || '';
  const capacity    = room.capacity    || '';
  const size        = room.size        || '';
  // Same guard as VenueListItemCard.jsx: a truthy non-array (e.g. a string) slips
  // past `|| []` and crashes at .map() below. Confirmed 548/552 production listings
  // carry listings.amenities as prose text, not an array; unproven but structurally
  // identical here, so guarding preemptively.
  const amenities   = (Array.isArray(room.amenities) ? room.amenities : []).slice(0, 8);

  const statsRow = (
    <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginTop: 18 }}>
      {size && (
        <div style={{
          fontFamily: FB, fontSize: 11, letterSpacing: '0.18em',
          textTransform: 'uppercase', color: palette.accent, fontWeight: 600,
        }}>
          {size}
        </div>
      )}
      {size && capacity && <div style={{ width: 1, background: palette.muted, opacity: 0.3 }} />}
      {capacity && (
        <div style={{
          fontFamily: FB, fontSize: 11, letterSpacing: '0.18em',
          textTransform: 'uppercase', color: palette.accent, fontWeight: 600,
        }}>
          {capacity}
        </div>
      )}
    </div>
  );

  // ── Layout A: cinematic full-bleed ─────────────────────────────────────────
  if (layoutIdx === 0) {
    return (
      <div style={{
        position: 'relative',
        width: '100%',
        height: isMobile ? '70vh' : '70vh',
        minHeight: isMobile ? 520 : 620,
        overflow: 'hidden',
        marginBottom: isMobile ? 48 : 64,
      }}>
        {primary ? (
          <img src={imgUrl(primary, { width: 1800 })} alt={title}
               style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div style={{ position: 'absolute', inset: 0, background: palette.bg3 }} />
        )}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.15) 55%, rgba(0,0,0,0.05) 100%)',
        }} />
        <div style={{
          position: 'absolute',
          left: 0, right: 0, bottom: isMobile ? 40 : 64,
          padding: isMobile ? '0 24px' : '0 7vw',
          color: '#f5f0e8',
        }}>
          <div style={{
            fontFamily: FB, fontSize: 10, letterSpacing: '0.28em',
            textTransform: 'uppercase', color: palette.accent,
            fontWeight: 600, marginBottom: 14,
          }}>
            Suite {String(index + 1).padStart(2, '0')}
          </div>
          <h3 style={{
            fontFamily: FD,
            fontSize: isMobile ? 36 : 56,
            lineHeight: 1.05, letterSpacing: '-0.02em',
            fontWeight: 400, margin: 0,
            maxWidth: '16ch',
          }}>
            {title}
          </h3>
          {description && (
            <p style={{
              fontFamily: FB, fontSize: isMobile ? 15 : 17,
              lineHeight: 1.7, fontWeight: 300,
              color: 'rgba(245,240,232,0.85)',
              marginTop: 20, marginBottom: 0,
              maxWidth: 600,
            }}>
              {description}
            </p>
          )}
          {(size || capacity) && (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 22 }}>
              {size && (
                <span style={{
                  display: 'inline-block',
                  padding: '7px 14px',
                  border: `1px solid ${palette.accent}`,
                  borderRadius: 999,
                  fontFamily: FB, fontSize: 10,
                  letterSpacing: '0.18em', textTransform: 'uppercase',
                  color: palette.accent, fontWeight: 600,
                }}>{size}</span>
              )}
              {capacity && (
                <span style={{
                  display: 'inline-block',
                  padding: '7px 14px',
                  border: '1px solid rgba(245,240,232,0.35)',
                  borderRadius: 999,
                  fontFamily: FB, fontSize: 10,
                  letterSpacing: '0.18em', textTransform: 'uppercase',
                  color: 'rgba(245,240,232,0.85)', fontWeight: 600,
                }}>{capacity}</span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Layout B: split 55/45 image-left ───────────────────────────────────────
  if (layoutIdx === 1) {
    return (
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '55% 45%',
        gap: 0,
        marginBottom: isMobile ? 48 : 88,
      }}>
        <div style={{
          position: 'relative',
          height: isMobile ? 360 : 640,
          overflow: 'hidden',
          background: palette.bg3,
        }}>
          {primary && (
            <img src={imgUrl(primary, { width: 1800 })} alt={title}
                 style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
          )}
        </div>
        <div style={{
          padding: isMobile ? '40px 24px 0' : '60px 7vw 60px 60px',
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
        }}>
          <div style={{
            fontFamily: FB, fontSize: 10, letterSpacing: '0.28em',
            textTransform: 'uppercase', color: palette.accent, fontWeight: 600,
          }}>
            Suite {String(index + 1).padStart(2, '0')}
          </div>
          <div style={{ width: 40, height: 1, background: palette.accent, marginTop: 16, marginBottom: 22 }} />
          <h3 style={{
            fontFamily: FD,
            fontSize: isMobile ? 32 : 46,
            lineHeight: 1.1, letterSpacing: '-0.015em',
            fontWeight: 400, margin: 0,
          }}>
            {title}
          </h3>
          {description && (
            <p style={{
              fontFamily: FB,
              fontSize: isMobile ? 15 : 17,
              lineHeight: 1.75, fontWeight: 300,
              color: palette.muted,
              marginTop: 22, marginBottom: 0,
            }}>
              {description}
            </p>
          )}
          {statsRow}
          {amenities.length > 0 && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '10px 24px',
              marginTop: 28,
              paddingTop: 28,
              borderTop: `1px solid ${palette.muted}`,
              borderTopColor: 'rgba(211,154,33,0.2)',
            }}>
              {amenities.map((a, i) => (
                <div key={i} className="flagship-room-amenity" style={{
                  fontFamily: FB, fontSize: 13,
                  color: palette.text, opacity: 0.85,
                  display: 'flex', alignItems: 'center', gap: 10,
                }}>
                  <span style={{ width: 4, height: 4, borderRadius: '50%', background: palette.accent, flexShrink: 0 }} />
                  {typeof a === 'string' ? a : a?.label || a?.name}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Layout C: editorial card pair (60% detail card / 40% image) ───────────
  if (layoutIdx === 2) {
    return (
      <div style={{
        padding: isMobile ? '0 24px' : '0 7vw',
        marginBottom: isMobile ? 48 : 88,
      }}>
        <div style={{
          maxWidth: 1280, margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '60% 40%',
          gap: isMobile ? 24 : 40,
          alignItems: 'stretch',
        }}>
          {/* Detail card */}
          <div style={{
            background: palette.mode === 'dark' ? palette.bg2 : palette.bg3,
            padding: isMobile ? '36px 28px' : '56px 60px',
            border: `1px solid ${palette.mode === 'dark' ? 'rgba(211,154,33,0.12)' : 'rgba(184,150,46,0.18)'}`,
          }}>
            <div style={{
              fontFamily: FB, fontSize: 10, letterSpacing: '0.28em',
              textTransform: 'uppercase', color: palette.accent, fontWeight: 600,
            }}>
              Suite {String(index + 1).padStart(2, '0')}
            </div>
            <div style={{ width: 40, height: 1, background: palette.accent, marginTop: 16, marginBottom: 24 }} />
            <h3 style={{
              fontFamily: FD,
              fontSize: isMobile ? 28 : 40,
              lineHeight: 1.1, letterSpacing: '-0.015em',
              fontWeight: 400, margin: 0,
            }}>
              {title}
            </h3>
            {description && (
              <p style={{
                fontFamily: FB, fontSize: isMobile ? 15 : 16,
                lineHeight: 1.75, fontWeight: 300,
                color: palette.muted,
                marginTop: 20, marginBottom: 0,
              }}>
                {description}
              </p>
            )}
            {(size || capacity) && (
              <div style={{
                display: 'flex', gap: 24, marginTop: 28,
                paddingTop: 24,
                borderTop: `1px solid ${palette.accent}`,
                borderTopColor: 'rgba(211,154,33,0.25)',
              }}>
                {size && (
                  <div>
                    <div style={{ fontFamily: FB, fontSize: 9, letterSpacing: '0.22em', textTransform: 'uppercase', color: palette.muted, marginBottom: 4 }}>Size</div>
                    <div style={{ fontFamily: FD, fontSize: 20, color: palette.text }}>{size}</div>
                  </div>
                )}
                {capacity && (
                  <div>
                    <div style={{ fontFamily: FB, fontSize: 9, letterSpacing: '0.22em', textTransform: 'uppercase', color: palette.muted, marginBottom: 4 }}>Capacity</div>
                    <div style={{ fontFamily: FD, fontSize: 20, color: palette.text }}>{capacity}</div>
                  </div>
                )}
              </div>
            )}
            {amenities.length > 0 && (
              <div style={{ marginTop: 24, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {amenities.slice(0, 6).map((a, i) => (
                  <span key={i} style={{
                    padding: '5px 11px',
                    border: '1px solid rgba(211,154,33,0.3)',
                    borderRadius: 999,
                    fontFamily: FB, fontSize: 10,
                    letterSpacing: '0.14em', textTransform: 'uppercase',
                    color: palette.text, opacity: 0.85,
                  }}>
                    {typeof a === 'string' ? a : a?.label || a?.name}
                  </span>
                ))}
              </div>
            )}
          </div>
          {/* Image column */}
          <div style={{
            position: 'relative',
            minHeight: isMobile ? 300 : 'auto',
            background: palette.bg3,
            overflow: 'hidden',
          }}>
            {primary && (
              <img src={imgUrl(primary, { width: 1800 })} alt={title}
                   style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Layout D: stacked mosaic ───────────────────────────────────────────────
  return (
    <div style={{
      padding: isMobile ? '0 24px' : '0 7vw',
      marginBottom: isMobile ? 48 : 88,
    }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        {/* Title block */}
        <div style={{ textAlign: 'center', marginBottom: isMobile ? 28 : 48 }}>
          <div style={{
            fontFamily: FB, fontSize: 10, letterSpacing: '0.28em',
            textTransform: 'uppercase', color: palette.accent, fontWeight: 600,
          }}>
            Suite {String(index + 1).padStart(2, '0')}
          </div>
          <div style={{ width: 40, height: 1, background: palette.accent, margin: '16px auto 22px' }} />
          <h3 style={{
            fontFamily: FD,
            fontSize: isMobile ? 32 : 48,
            lineHeight: 1.05, letterSpacing: '-0.015em',
            fontWeight: 400, margin: 0,
          }}>
            {title}
          </h3>
          {description && (
            <p style={{
              fontFamily: FB, fontSize: isMobile ? 15 : 17,
              lineHeight: 1.7, fontWeight: 300,
              color: palette.muted,
              marginTop: 22, marginBottom: 0,
              maxWidth: 720, marginLeft: 'auto', marginRight: 'auto',
            }}>
              {description}
            </p>
          )}
          {statsRow && (size || capacity) && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>{statsRow}</div>
          )}
        </div>

        {/* 3-image mosaic */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr',
          gridTemplateRows: isMobile ? 'auto auto auto' : '1fr 1fr',
          gap: isMobile ? 12 : 16,
          height: isMobile ? 'auto' : 560,
        }}>
          <div style={{
            gridRow: isMobile ? 'auto' : '1 / 3',
            background: palette.bg3,
            overflow: 'hidden',
            position: 'relative',
            height: isMobile ? 260 : 'auto',
          }}>
            {primary && (
              <img src={imgUrl(primary, { width: 1800 })} alt=""
                   style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
            )}
          </div>
          <div style={{ background: palette.bg3, overflow: 'hidden', position: 'relative', height: isMobile ? 200 : 'auto' }}>
            {secondary && (
              <img src={imgUrl(secondary, { width: 1200 })} alt=""
                   style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
            )}
          </div>
          <div style={{ background: palette.bg3, overflow: 'hidden', position: 'relative', height: isMobile ? 200 : 'auto' }}>
            {tertiary && (
              <img src={imgUrl(tertiary, { width: 1200 })} alt=""
                   style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// GALLERY, editorial mosaic with lightbox
// ─────────────────────────────────────────────────────────────────────────────
// Uses the same gallery component the default showcase renders below the hero
// (MediaBlock → ImageGallery). Light-theme context so ImageGallery's chrome
// renders correctly, matching the "Gallery / 50 photographs" block.
function FlagshipGallery({ content, palette }) {
  const { isMobile } = useBreakpoint();
  const images = (content.images || []).filter(i => i && i.url);
  if (!images.length) return null;

  const label    = content.label    || content.eyebrow || 'Gallery';
  const title    = content.title    || 'A Portrait in Frames';
  const subtitle = content.subtitle || `A portrait in ${images.length} frames`;

  const galleryItems = images.map((item, i) => ({
    id:  `showcase-img-${i}`,
    src: item.url,
    alt: item.caption || '',
  }));

  return (
    <section style={{
      background: '#f2f0ea',
      color: '#1C1410',
      padding: isMobile ? '70px 0 90px' : '120px 0 140px',
    }}>
      {/* Header */}
      <div style={{
        padding: isMobile ? '0 24px 40px' : '0 7vw 64px',
        maxWidth: 1280, margin: '0 auto',
      }}>
        <div style={{
          fontFamily: FB, fontSize: 10, letterSpacing: '0.28em',
          textTransform: 'uppercase', color: '#B8962E', fontWeight: 600,
        }}>{label}</div>
        <div style={{ width: 40, height: 1, background: '#B8962E', marginTop: 16, marginBottom: 22 }} />
        <h2 style={{
          fontFamily: FD,
          fontSize: isMobile ? 38 : 56,
          lineHeight: 1.05, letterSpacing: '-0.02em',
          fontWeight: 400, margin: 0, maxWidth: '16ch', color: '#1C1410',
        }}>
          {title}
        </h2>
        {subtitle && (
          <p style={{
            fontFamily: FB, fontSize: isMobile ? 14 : 16,
            lineHeight: 1.6, fontWeight: 300,
            color: 'rgba(28,20,16,0.6)',
            marginTop: 14, marginBottom: 0, fontStyle: 'italic',
          }}>
            {subtitle}
          </p>
        )}
      </div>

      {/* Same gallery as the default showcase renderer */}
      <div style={{ padding: isMobile ? '0 24px' : '0 7vw' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <ThemeCtx.Provider value={LIGHT}>
            <MediaBlock videos={[]} gallery={galleryItems} />
          </ThemeCtx.Provider>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CTA, decision moment with inline form shell
// ─────────────────────────────────────────────────────────────────────────────
function FlagshipCta({ content, venueName, palette, showcaseHero, listingFirstImage }) {
  const { isMobile } = useBreakpoint();
  const bgImage = resolveImage(content.image, showcaseHero, listingFirstImage);
  const headline = content.headline || content.title || `Begin your enquiry with ${venueName}`;
  const subhead  = content.subhead  || content.body  ||
    'Every request is read personally. We respond within 24 hours with a bespoke proposal tailored to your vision, privately and discreetly.';
  const pillars = content.pillars || [
    'Private enquiry',
    'Personal reply within 24h',
    'Bespoke proposal',
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    const form = e.target;
    const data = {
      firstName:    form.firstName.value,
      email:        form.email.value,
      weddingDate:  form.weddingDate.value,
      vision:       form.vision.value,
    };
    if (typeof content.onEnquire === 'function') content.onEnquire(data);
    else console.info('[FlagshipCta] enquiry submission', data);
  };

  return (
    <section style={{
      position: 'relative',
      background: '#0a0806',
      color: '#f5f0e8',
      padding: isMobile ? '80px 24px' : '140px 7vw',
      overflow: 'hidden',
    }}>
      {bgImage && (
        <>
          <img
            src={imgUrl(bgImage, { width: 1800 })} alt=""
            style={{
              position: 'absolute', inset: 0,
              width: '100%', height: '100%', objectFit: 'cover',
            }}
          />
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(135deg, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.65) 100%)',
          }} />
        </>
      )}

      <div style={{
        position: 'relative',
        maxWidth: 1280, margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1.1fr 1fr',
        gap: isMobile ? 48 : 80,
        alignItems: 'center',
      }}>
        {/* Left, headline + pillars */}
        <div>
          <div style={{
            fontFamily: FB, fontSize: 10, letterSpacing: '0.28em',
            textTransform: 'uppercase', color: palette.accent, fontWeight: 600,
          }}>
            Enquire
          </div>
          <div style={{ width: 40, height: 1, background: palette.accent, marginTop: 16, marginBottom: 24 }} />
          <h2 style={{
            fontFamily: FD,
            fontSize: isMobile ? 36 : 56,
            lineHeight: 1.05, letterSpacing: '-0.02em',
            fontWeight: 400, margin: 0,
            maxWidth: '18ch',
          }}>
            {headline}
          </h2>
          <p style={{
            fontFamily: FB,
            fontSize: isMobile ? 15 : 17,
            lineHeight: 1.75, fontWeight: 300,
            color: 'rgba(245,240,232,0.8)',
            marginTop: 24, marginBottom: 36,
            maxWidth: 520,
          }}>
            {subhead}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {pillars.map((p, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 14,
                fontFamily: FB, fontSize: 11,
                letterSpacing: '0.22em', textTransform: 'uppercase',
                color: 'rgba(245,240,232,0.92)', fontWeight: 600,
              }}>
                <span style={{
                  width: 8, height: 8, borderRadius: '50%',
                  background: palette.accent, flexShrink: 0,
                }} />
                {p}
              </div>
            ))}
          </div>
        </div>

        {/* Right, form shell */}
        <form
          onSubmit={handleSubmit}
          style={{
            background: 'rgba(10,8,6,0.58)',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            border: '1px solid rgba(211,154,33,0.2)',
            padding: isMobile ? '32px 24px' : '44px 48px',
          }}
        >
          <div style={{
            fontFamily: FB, fontSize: 10, letterSpacing: '0.28em',
            textTransform: 'uppercase', color: palette.accent, fontWeight: 600,
            marginBottom: 28,
          }}>
            Private Enquiry
          </div>

          <FlagshipField label="First Name" name="firstName" placeholder="Sofia" palette={palette} />
          <FlagshipField label="Email"      name="email"     type="email" placeholder="sofia@example.com" palette={palette} />
          <FlagshipField label="Wedding Date" name="weddingDate" type="date" palette={palette} />
          <FlagshipField
            label="Your Vision"
            name="vision"
            textarea
            placeholder="Share anything we should know: guest count, style, dates you're weighing."
            palette={palette}
          />

          <button
            type="submit"
            style={{
              width: '100%', marginTop: 12,
              padding: '18px 22px',
              background: `linear-gradient(135deg, ${palette.accent} 0%, #d4b357 50%, ${palette.accent} 100%)`,
              backgroundSize: '200% 100%',
              border: 'none', color: '#0a0806',
              fontFamily: FB, fontSize: 11,
              letterSpacing: '0.28em', textTransform: 'uppercase',
              fontWeight: 700, cursor: 'pointer',
              transition: 'all 320ms ease',
              animation: 'flagship-gold-sweep 5s ease-in-out infinite alternate',
            }}
          >
            Send Enquiry
          </button>
        </form>
      </div>

      {/* Concierge line */}
      <div style={{
        position: 'relative',
        maxWidth: 1280, margin: '48px auto 0',
        textAlign: 'center',
      }}>
        <a
          href="#"
          style={{
            fontFamily: FB, fontSize: 12,
            letterSpacing: '0.2em', textTransform: 'uppercase',
            color: 'rgba(245,240,232,0.78)',
            textDecoration: 'none', fontWeight: 500,
            display: 'inline-flex', alignItems: 'center', gap: 10,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M20.5 3.5A11.8 11.8 0 0 0 12 0C5.4 0 0 5.4 0 12c0 2.1.5 4.2 1.6 6L0 24l6.2-1.6c1.7.9 3.7 1.4 5.8 1.4 6.6 0 12-5.4 12-12 0-3.2-1.3-6.2-3.5-8.4zM12 22c-1.9 0-3.7-.5-5.3-1.4l-.4-.2-3.9 1 1-3.8-.2-.4a10 10 0 1 1 18.8-5.2A10 10 0 0 1 12 22zm5.5-7.5c-.3-.2-1.8-.9-2-1s-.5-.1-.7.1c-.2.3-.8.9-1 1.1-.2.2-.3.2-.6.1a8.2 8.2 0 0 1-4-3.5c-.3-.5.3-.5.9-1.5.1-.2 0-.4 0-.6L9 6.8c-.2-.4-.4-.4-.6-.4h-.5c-.2 0-.4.1-.7.3A3.1 3.1 0 0 0 6.1 9c0 1.7 1.3 3.4 1.4 3.6.2.2 2.6 4 6.2 5.6 2.3 1 3.2 1 4.3.8a3 3 0 0 0 2-1.4c.2-.5.2-1 .2-1.1-.1-.2-.3-.2-.6-.4z" fill={palette.accent}/>
          </svg>
          Or speak directly to a wedding concierge
          <span style={{ color: palette.accent }}>→</span>
        </a>
      </div>
    </section>
  );
}

function FlagshipField({ label, name, type = 'text', placeholder, textarea, palette }) {
  const common = {
    width: '100%',
    padding: '12px 0',
    background: 'transparent',
    border: 'none',
    borderBottom: '1px solid rgba(211,154,33,0.35)',
    color: '#f5f0e8',
    fontFamily: FB,
    fontSize: 14,
    fontWeight: 300,
    resize: 'none',
  };
  return (
    <div style={{ marginBottom: 22 }}>
      <label style={{
        display: 'block',
        fontFamily: FB, fontSize: 9,
        letterSpacing: '0.28em', textTransform: 'uppercase',
        color: palette.accent, fontWeight: 600,
        marginBottom: 6,
      }}>{label}</label>
      {textarea ? (
        <textarea
          name={name} placeholder={placeholder}
          rows={3}
          className="flagship-cta-input"
          style={common}
        />
      ) : (
        <input
          name={name} type={type} placeholder={placeholder}
          className="flagship-cta-input"
          style={common}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Simple elevated sections (stats, quote, feature, mosaic, highlight-band, etc)
// ─────────────────────────────────────────────────────────────────────────────

function FlagshipStats({ content, palette }) {
  const { isMobile } = useBreakpoint();
  const stats = (content.stats || content.items || []).filter(s => s && (s.value || s.label));
  if (!stats.length) return null;
  return (
    <section style={{
      background: '#0a0806', color: '#f5f0e8',
      padding: isMobile ? '70px 24px' : '110px 7vw',
    }}>
      <div style={{
        maxWidth: 1280, margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr 1fr' : `repeat(${Math.min(stats.length, 4)}, 1fr)`,
        gap: isMobile ? 32 : 0,
      }}>
        {stats.map((s, i) => (
          <div key={i} style={{
            padding: isMobile ? '0' : '0 32px',
            textAlign: 'center',
            borderLeft: !isMobile && i > 0 ? '1px solid rgba(211,154,33,0.2)' : 'none',
          }}>
            <div style={{
              fontFamily: FD,
              fontSize: isMobile ? 48 : 72,
              lineHeight: 1.0, letterSpacing: '-0.02em',
              color: palette.accent, fontWeight: 400,
            }}>
              {s.value}
            </div>
            <div style={{
              fontFamily: FB, fontSize: 10,
              letterSpacing: '0.28em', textTransform: 'uppercase',
              color: 'rgba(245,240,232,0.7)', fontWeight: 600,
              marginTop: 14,
            }}>
              {s.label}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function FlagshipQuote({ content, palette, showcaseHero, listingFirstImage }) {
  const { isMobile } = useBreakpoint();
  const bg = resolveImage(content.image, showcaseHero, listingFirstImage);
  const text       = content.quote || content.text || content.body || '';
  const attribution = content.attribution || content.author || '';
  if (!text) return null;
  return (
    <section style={{
      position: 'relative',
      padding: isMobile ? '120px 24px' : '180px 7vw',
      color: '#f5f0e8',
      background: '#0a0806',
      overflow: 'hidden',
    }}>
      {bg && (
        <>
          <img src={imgUrl(bg, { width: 1800 })} alt=""
               style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.68)' }} />
        </>
      )}
      <div style={{
        position: 'relative', maxWidth: 900, margin: '0 auto',
        textAlign: 'center',
      }}>
        <div style={{
          fontFamily: FD, fontSize: 72, lineHeight: 1,
          color: palette.accent, opacity: 0.7,
          marginBottom: 20, fontStyle: 'italic',
        }}>
          &ldquo;
        </div>
        <blockquote style={{
          fontFamily: FD,
          fontSize: isMobile ? 24 : 36,
          lineHeight: 1.35, fontStyle: 'italic',
          fontWeight: 300, color: '#f5f0e8',
          margin: 0,
        }}>
          {text}
        </blockquote>
        {attribution && (
          <>
            <div style={{ width: 40, height: 1, background: palette.accent, margin: '36px auto 20px' }} />
            <div style={{
              fontFamily: FB, fontSize: 11,
              letterSpacing: '0.28em', textTransform: 'uppercase',
              color: 'rgba(245,240,232,0.78)', fontWeight: 600,
            }}>
              {attribution}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function FlagshipHighlightBand({ content, palette }) {
  const { isMobile } = useBreakpoint();
  const items = (content.items || content.highlights || []).filter(Boolean);
  if (!items.length) return null;
  return (
    <section style={{
      background: palette.mode === 'dark' ? '#0a0806' : '#1a1410',
      color: '#f5f0e8',
      padding: isMobile ? '70px 24px' : '110px 7vw',
    }}>
      {content.title && (
        <div style={{ textAlign: 'center', maxWidth: 900, margin: '0 auto 60px' }}>
          {content.label && (
            <div style={{
              fontFamily: FB, fontSize: 10, letterSpacing: '0.28em',
              textTransform: 'uppercase', color: palette.accent, fontWeight: 600,
            }}>{content.label}</div>
          )}
          {content.label && <div style={{ width: 40, height: 1, background: palette.accent, margin: '16px auto 22px' }} />}
          <h2 style={{
            fontFamily: FD,
            fontSize: isMobile ? 36 : 52,
            lineHeight: 1.08, letterSpacing: '-0.02em',
            fontWeight: 400, margin: 0,
          }}>{content.title}</h2>
        </div>
      )}
      <div style={{
        maxWidth: 1280, margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : `repeat(${Math.min(items.length, 4)}, 1fr)`,
        gap: isMobile ? 36 : 44,
      }}>
        {items.slice(0, 4).map((it, i) => (
          <div key={i}>
            <div style={{
              fontFamily: FB, fontSize: 10, letterSpacing: '0.28em',
              textTransform: 'uppercase', color: palette.accent, fontWeight: 600,
              marginBottom: 14,
            }}>
              {String(i + 1).padStart(2, '0')}, {it.label || it.eyebrow || 'Note'}
            </div>
            <h3 style={{
              fontFamily: FD,
              fontSize: isMobile ? 24 : 28,
              lineHeight: 1.2, letterSpacing: '-0.01em',
              fontWeight: 400, margin: 0,
            }}>
              {it.title || it.headline}
            </h3>
            {(it.description || it.body) && (
              <p style={{
                fontFamily: FB, fontSize: 14,
                lineHeight: 1.7, fontWeight: 300,
                color: 'rgba(245,240,232,0.72)',
                marginTop: 14, marginBottom: 0,
              }}>
                {it.description || it.body}
              </p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function FlagshipFeature({ content, palette, showcaseHero, listingFirstImage, heightVh = 54 }) {
  const { isMobile } = useBreakpoint();
  const img = resolveImage(content.image, showcaseHero, listingFirstImage);
  const direction = content.direction === 'right' ? 'right' : 'left';
  const imgLeft   = direction === 'left';
  const title = content.title || content.headline || '';
  const body  = content.body  || content.text || '';
  const label = content.label || content.eyebrow || '';
  const bullets = Array.isArray(content.bullets) ? content.bullets.filter(Boolean) : [];

  const imagePanel = (
    <div style={{
      position: 'relative',
      minHeight: isMobile ? 320 : `${heightVh}vh`,
      background: palette.bg3,
      overflow: 'hidden',
    }}>
      {img && (
        <img src={imgUrl(img, { width: 1800 })} alt={title}
             style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
      )}
    </div>
  );
  const textPanel = (
    <div style={{
      padding: isMobile ? '40px 24px' : '60px 7vw',
      display: 'flex', flexDirection: 'column', justifyContent: 'center',
      background: palette.bg,
      color: palette.text,
    }}>
      {label && (
        <>
          <div style={{
            fontFamily: FB, fontSize: 10, letterSpacing: '0.28em',
            textTransform: 'uppercase', color: palette.accent, fontWeight: 600,
          }}>{label}</div>
          <div style={{ width: 40, height: 1, background: palette.accent, marginTop: 16, marginBottom: 22 }} />
        </>
      )}
      {title && (
        <h2 style={{
          fontFamily: FD,
          fontSize: isMobile ? 32 : 44,
          lineHeight: 1.1, letterSpacing: '-0.015em',
          fontWeight: 400, margin: 0,
        }}>{title}</h2>
      )}
      {body && (
        <p style={{
          fontFamily: FB,
          fontSize: isMobile ? 15 : 17,
          lineHeight: 1.75, fontWeight: 300,
          color: palette.muted,
          marginTop: 22, marginBottom: 0,
        }}>{body}</p>
      )}
      {bullets.length > 0 && (
        <ul style={{ listStyle: 'none', margin: '24px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {bullets.map((b, i) => (
            <li key={i} style={{
              fontFamily: FB, fontSize: isMobile ? 15 : 16.5, lineHeight: 1.5,
              fontWeight: 300, color: palette.text,
              display: 'flex', alignItems: 'flex-start', gap: 12,
            }}>
              <span style={{ color: palette.accent, fontSize: 14, lineHeight: 1.6, flexShrink: 0 }}>✓</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <section style={{ background: palette.bg }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
      }}>
        {imgLeft ? <>{imagePanel}{textPanel}</> : <>{textPanel}{imagePanel}</>}
      </div>
    </section>
  );
}

function FlagshipMosaic({ content, palette, showcaseHero, listingFirstImage }) {
  const { isMobile } = useBreakpoint();
  const imgs = (content.images || []).map(i => typeof i === 'string' ? i : i?.url).filter(Boolean);
  const p = imgs[0] || showcaseHero || listingFirstImage;
  const s = imgs[1] || p;
  const t = imgs[2] || s;
  const title = content.title || content.headline || '';
  const body  = content.body  || content.text || '';
  const label = content.label || content.eyebrow || '';

  return (
    <section style={{
      background: palette.bg,
      color: palette.text,
      padding: isMobile ? '70px 24px' : '120px 7vw',
    }}>
      <div style={{ maxWidth: 1400, margin: '0 auto' }}>
        {(label || title) && (
          <div style={{ maxWidth: 720, marginBottom: isMobile ? 36 : 56 }}>
            {label && (
              <>
                <div style={{
                  fontFamily: FB, fontSize: 10, letterSpacing: '0.28em',
                  textTransform: 'uppercase', color: palette.accent, fontWeight: 600,
                }}>{label}</div>
                <div style={{ width: 40, height: 1, background: palette.accent, marginTop: 16, marginBottom: 22 }} />
              </>
            )}
            {title && (
              <h2 style={{
                fontFamily: FD,
                fontSize: isMobile ? 34 : 48,
                lineHeight: 1.08, letterSpacing: '-0.02em',
                fontWeight: 400, margin: 0,
              }}>{title}</h2>
            )}
            {body && (
              <p style={{
                fontFamily: FB,
                fontSize: isMobile ? 15 : 17,
                lineHeight: 1.75, fontWeight: 300,
                color: palette.muted,
                marginTop: 20, marginBottom: 0,
              }}>{body}</p>
            )}
          </div>
        )}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1.6fr 1fr',
          gridTemplateRows: isMobile ? 'auto auto auto' : '1fr 1fr',
          gap: isMobile ? 12 : 18,
          height: isMobile ? 'auto' : 620,
        }}>
          <div style={{
            gridRow: isMobile ? 'auto' : '1 / 3',
            overflow: 'hidden', background: palette.bg3,
            position: 'relative', minHeight: isMobile ? 300 : 'auto',
          }}>
            {p && <img src={imgUrl(p, { width: 1200 })} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />}
          </div>
          <div style={{ overflow: 'hidden', background: palette.bg3, position: 'relative', minHeight: isMobile ? 220 : 'auto' }}>
            {s && <img src={imgUrl(s, { width: 1200 })} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />}
          </div>
          <div style={{ overflow: 'hidden', background: palette.bg3, position: 'relative', minHeight: isMobile ? 220 : 'auto' }}>
            {t && <img src={imgUrl(t, { width: 1200 })} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />}
          </div>
        </div>
      </div>
    </section>
  );
}

function FlagshipVerified({ content, palette }) {
  const { isMobile } = useBreakpoint();
  const rows = (content.rows || content.items || []).filter(r => r && (r.label || r.value));
  if (!rows.length) return null;
  return (
    <section style={{
      background: palette.bg2,
      color: palette.text,
      padding: isMobile ? '70px 24px' : '120px 7vw',
    }}>
      <div style={{ maxWidth: 960, margin: '0 auto' }}>
        <div style={{
          fontFamily: FB, fontSize: 10, letterSpacing: '0.28em',
          textTransform: 'uppercase', color: palette.accent, fontWeight: 600,
        }}>{content.label || 'Verified Details'}</div>
        <div style={{ width: 40, height: 1, background: palette.accent, marginTop: 16, marginBottom: 24 }} />
        {content.title && (
          <h2 style={{
            fontFamily: FD,
            fontSize: isMobile ? 32 : 44,
            lineHeight: 1.1, letterSpacing: '-0.015em',
            fontWeight: 400, margin: '0 0 48px',
          }}>{content.title}</h2>
        )}
        <div>
          {rows.map((r, i) => (
            <div key={i} style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '240px 1fr',
              gap: isMobile ? 6 : 40,
              padding: '22px 0',
              borderBottom: `1px solid rgba(211,154,33,0.22)`,
              alignItems: 'baseline',
            }}>
              <div style={{
                fontFamily: FB, fontSize: 11,
                letterSpacing: '0.22em', textTransform: 'uppercase',
                color: palette.accent, fontWeight: 600,
              }}>{r.label}</div>
              <div style={{
                fontFamily: FD,
                fontSize: isMobile ? 18 : 22,
                lineHeight: 1.4, fontWeight: 400,
                color: palette.text,
              }}>{r.value}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FlagshipNearby({ content, palette }) {
  const { isMobile } = useBreakpoint();
  const items = (content.items || content.landmarks || []).filter(Boolean);
  if (!items.length) return null;
  return (
    <section style={{
      background: palette.bg,
      color: palette.text,
      padding: isMobile ? '70px 24px' : '120px 7vw',
    }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div style={{ maxWidth: 720, marginBottom: isMobile ? 36 : 56 }}>
          <div style={{
            fontFamily: FB, fontSize: 10, letterSpacing: '0.28em',
            textTransform: 'uppercase', color: palette.accent, fontWeight: 600,
          }}>{content.label || 'Neighbourhood'}</div>
          <div style={{ width: 40, height: 1, background: palette.accent, marginTop: 16, marginBottom: 22 }} />
          {content.title && (
            <h2 style={{
              fontFamily: FD,
              fontSize: isMobile ? 32 : 44,
              lineHeight: 1.08, letterSpacing: '-0.02em',
              fontWeight: 400, margin: 0,
            }}>{content.title}</h2>
          )}
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
          gap: isMobile ? 24 : 36,
        }}>
          {items.map((it, i) => (
            <div key={i} style={{
              padding: '22px 0 0',
              borderTop: `1px solid rgba(211,154,33,0.22)`,
            }}>
              <div style={{
                fontFamily: FD,
                fontSize: isMobile ? 20 : 24,
                lineHeight: 1.2, fontWeight: 400,
                color: palette.text,
              }}>{it.name || it.title}</div>
              {it.distance && (
                <div style={{
                  fontFamily: FB, fontSize: 11,
                  letterSpacing: '0.22em', textTransform: 'uppercase',
                  color: palette.accent, fontWeight: 600,
                  marginTop: 8,
                }}>{it.distance}</div>
              )}
              {it.description && (
                <p style={{
                  fontFamily: FB, fontSize: 13,
                  lineHeight: 1.65, fontWeight: 300,
                  color: palette.muted,
                  marginTop: 10, marginBottom: 0,
                }}>{it.description}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FlagshipMap({ content, palette, showcaseName, showcaseLocation }) {
  const { isMobile } = useBreakpoint();
  const mapEl  = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const address = content.address || showcaseLocation || showcaseName || '';
    const zoom = parseInt(content.zoom) || 14;

    async function init(lat, lng) {
      const L = await loadLeafletOnce();
      if (cancelled || !mapEl.current || !L) return;
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
      const map = L.map(mapEl.current, { zoomControl: true, scrollWheelZoom: false });
      mapRef.current = map;
      L.tileLayer(`https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${import.meta.env.VITE_CARTO_API_KEY}`, {
        attribution: '&copy; CARTO &copy; OSM', maxZoom: 19,
      }).addTo(map);
      map.setView([lat, lng], zoom);
      const icon = L.divIcon({
        className: '',
        iconSize: [30, 40], iconAnchor: [15, 40],
        html: `<svg viewBox="0 0 28 38" width="30" height="40" xmlns="http://www.w3.org/2000/svg">
          <path d="M14 1C7.1 1 1.5 6.6 1.5 13.5c0 4.2 2 7.9 5.1 10.3L14 37l7.4-13.2A12.4 12.4 0 0 0 26.5 13.5C26.5 6.6 20.9 1 14 1z" fill="${palette.accent}"/>
          <circle cx="14" cy="13.5" r="4.5" fill="white"/>
        </svg>`,
      });
      L.marker([lat, lng], { icon }).addTo(map);
    }

    async function geocode() {
      if (!address) return;
      try {
        const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(address)}`);
        const j = await r.json();
        if (cancelled) return;
        if (j && j[0]) init(parseFloat(j[0].lat), parseFloat(j[0].lon));
      } catch (_) {}
    }
    geocode();
    return () => { cancelled = true; if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } };
  }, [content.address, content.zoom, showcaseLocation, showcaseName, palette.accent]);

  return (
    <section style={{
      background: palette.bg, color: palette.text,
      padding: isMobile ? '70px 24px' : '120px 7vw',
    }}>
      <div style={{ maxWidth: 1400, margin: '0 auto' }}>
        <div style={{ maxWidth: 720, marginBottom: isMobile ? 36 : 48 }}>
          <div style={{
            fontFamily: FB, fontSize: 10, letterSpacing: '0.28em',
            textTransform: 'uppercase', color: palette.accent, fontWeight: 600,
          }}>{content.label || 'Location'}</div>
          <div style={{ width: 40, height: 1, background: palette.accent, marginTop: 16, marginBottom: 22 }} />
          {content.title && (
            <h2 style={{
              fontFamily: FD, fontSize: isMobile ? 32 : 44,
              lineHeight: 1.08, letterSpacing: '-0.02em',
              fontWeight: 400, margin: 0,
            }}>{content.title}</h2>
          )}
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1.8fr 1fr',
          gap: isMobile ? 24 : 36,
        }}>
          <div ref={mapEl} style={{
            height: isMobile ? 320 : 480,
            background: palette.bg3,
            border: `1px solid rgba(211,154,33,0.2)`,
          }} />
          <div style={{
            padding: isMobile ? 24 : 36,
            background: palette.bg2,
            border: `1px solid rgba(211,154,33,0.2)`,
            display: 'flex', flexDirection: 'column', justifyContent: 'center',
          }}>
            <div style={{
              fontFamily: FB, fontSize: 10, letterSpacing: '0.28em',
              textTransform: 'uppercase', color: palette.accent, fontWeight: 600,
              marginBottom: 14,
            }}>Address</div>
            <div style={{
              fontFamily: FD, fontSize: isMobile ? 18 : 22,
              lineHeight: 1.45, color: palette.text,
              whiteSpace: 'pre-wrap',
            }}>{content.address || showcaseLocation}</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FlagshipFilms({ content, palette }) {
  const { isMobile } = useBreakpoint();
  const films = (content.films || content.videos || []).filter(f => f && (f.url || f.videoUrl || f.embedUrl));
  if (!films.length) return null;
  return (
    <section style={{
      background: '#0a0806', color: '#f5f0e8',
      padding: isMobile ? '70px 24px' : '120px 7vw',
    }}>
      <div style={{ maxWidth: 1400, margin: '0 auto' }}>
        <div style={{ maxWidth: 720, marginBottom: isMobile ? 36 : 56 }}>
          <div style={{
            fontFamily: FB, fontSize: 10, letterSpacing: '0.28em',
            textTransform: 'uppercase', color: palette.accent, fontWeight: 600,
          }}>{content.label || 'Films'}</div>
          <div style={{ width: 40, height: 1, background: palette.accent, marginTop: 16, marginBottom: 22 }} />
          {content.title && (
            <h2 style={{
              fontFamily: FD, fontSize: isMobile ? 36 : 52,
              lineHeight: 1.05, letterSpacing: '-0.02em',
              fontWeight: 400, margin: 0,
            }}>{content.title}</h2>
          )}
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
          gap: isMobile ? 24 : 28,
        }}>
          {films.map((f, i) => {
            const url = f.url || f.videoUrl || f.embedUrl;
            const vid = parseVideoUrl(url);
            const src = vid?.type === 'youtube'
              ? `https://www.youtube.com/embed/${vid.id}?rel=0`
              : vid?.type === 'vimeo'
                ? `https://player.vimeo.com/video/${vid.id}`
                : null;
            return (
              <div key={i}>
                <div style={{ aspectRatio: '16/9', background: '#000', position: 'relative', overflow: 'hidden' }}>
                  {src ? (
                    <iframe src={src} allow="fullscreen"
                            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }} />
                  ) : vid?.type === 'direct' ? (
                    <video src={vid.url} controls
                           style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
                  ) : null}
                </div>
                {(f.title || f.caption) && (
                  <div style={{ paddingTop: 18 }}>
                    {f.title && (
                      <div style={{
                        fontFamily: FD, fontSize: 20,
                        fontWeight: 400, color: '#f5f0e8',
                      }}>{f.title}</div>
                    )}
                    {f.caption && (
                      <div style={{
                        fontFamily: FB, fontSize: 13,
                        lineHeight: 1.6, fontWeight: 300,
                        color: 'rgba(245,240,232,0.7)',
                        marginTop: 6, fontStyle: 'italic',
                      }}>{f.caption}</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FlagshipBentoGrid({ content, palette }) {
  const { isMobile } = useBreakpoint();
  const cells = (content.cells || []).filter(c => c && (c.url || c.image || c.videoUrl));
  if (!cells.length) return null;
  return (
    <section style={{
      background: palette.bg, color: palette.text,
      padding: isMobile ? '70px 24px' : '120px 7vw',
    }}>
      <div style={{ maxWidth: 1400, margin: '0 auto' }}>
        {content.title && (
          <div style={{ marginBottom: 40, maxWidth: 720 }}>
            {content.label && (
              <>
                <div style={{
                  fontFamily: FB, fontSize: 10, letterSpacing: '0.28em',
                  textTransform: 'uppercase', color: palette.accent, fontWeight: 600,
                }}>{content.label}</div>
                <div style={{ width: 40, height: 1, background: palette.accent, marginTop: 16, marginBottom: 22 }} />
              </>
            )}
            <h2 style={{
              fontFamily: FD, fontSize: isMobile ? 32 : 44,
              lineHeight: 1.1, letterSpacing: '-0.015em',
              fontWeight: 400, margin: 0,
            }}>{content.title}</h2>
          </div>
        )}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
          gridAutoRows: isMobile ? 'auto' : '180px',
          gap: isMobile ? 10 : 16,
        }}>
          {cells.map((c, i) => {
            const span = c.span || (i % 5 === 0 ? 2 : 1);
            const rowSpan = c.rowSpan || (i % 3 === 0 ? 2 : 1);
            return (
              <div key={i} style={{
                gridColumn: isMobile ? 'auto' : `span ${span}`,
                gridRow: isMobile ? 'auto' : `span ${rowSpan}`,
                overflow: 'hidden', background: palette.bg3,
                position: 'relative',
                minHeight: isMobile ? 200 : 'auto',
              }}>
                {c.url || c.image ? (
                  <img src={imgUrl(c.url || c.image, { width: 1200 })} alt={c.caption || ''}
                       style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : null}
                {c.caption && (
                  <div style={{
                    position: 'absolute', left: 0, right: 0, bottom: 0,
                    padding: '16px 18px',
                    background: 'linear-gradient(to top, rgba(0,0,0,0.85), transparent)',
                    color: '#f5f0e8',
                    fontFamily: FB, fontSize: 12,
                    letterSpacing: '0.08em',
                  }}>{c.caption}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FlagshipPricing({ content, palette }) {
  const { isMobile } = useBreakpoint();
  const tiers = (content.tiers || content.items || []).filter(Boolean);
  if (!tiers.length) return null;
  return (
    <section style={{
      background: palette.bg2, color: palette.text,
      padding: isMobile ? '70px 24px' : '120px 7vw',
    }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        {content.title && (
          <div style={{ textAlign: 'center', marginBottom: 56, maxWidth: 720, marginLeft: 'auto', marginRight: 'auto' }}>
            <div style={{
              fontFamily: FB, fontSize: 10, letterSpacing: '0.28em',
              textTransform: 'uppercase', color: palette.accent, fontWeight: 600,
            }}>{content.label || 'Investment'}</div>
            <div style={{ width: 40, height: 1, background: palette.accent, margin: '16px auto 22px' }} />
            <h2 style={{
              fontFamily: FD, fontSize: isMobile ? 32 : 44,
              lineHeight: 1.1, letterSpacing: '-0.015em',
              fontWeight: 400, margin: 0,
            }}>{content.title}</h2>
          </div>
        )}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : `repeat(${Math.min(tiers.length, 3)}, 1fr)`,
          gap: isMobile ? 18 : 28,
        }}>
          {tiers.map((t, i) => (
            <div key={i} style={{
              background: palette.bg,
              padding: isMobile ? '32px 28px' : '44px 40px',
              border: `1px solid rgba(211,154,33,0.2)`,
            }}>
              <div style={{
                fontFamily: FB, fontSize: 10, letterSpacing: '0.28em',
                textTransform: 'uppercase', color: palette.accent, fontWeight: 600,
                marginBottom: 14,
              }}>{t.label || t.tier}</div>
              <div style={{
                fontFamily: FD, fontSize: isMobile ? 36 : 48,
                fontWeight: 400, color: palette.text,
                letterSpacing: '-0.02em', lineHeight: 1,
              }}>{t.price || t.value}</div>
              {t.description && (
                <p style={{
                  fontFamily: FB, fontSize: 14,
                  lineHeight: 1.7, fontWeight: 300,
                  color: palette.muted,
                  marginTop: 20, marginBottom: 0,
                }}>{t.description}</p>
              )}
              {(t.includes || t.features) && (
                <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {(t.includes || t.features).map((f, j) => (
                    <div key={j} style={{
                      fontFamily: FB, fontSize: 13,
                      color: palette.text, opacity: 0.85,
                      display: 'flex', alignItems: 'center', gap: 10,
                    }}>
                      <span style={{ width: 4, height: 4, borderRadius: '50%', background: palette.accent, flexShrink: 0 }} />
                      {f}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FlagshipFaq({ content, palette }) {
  const { isMobile } = useBreakpoint();
  const faqs = (content.faqs || content.items || []).filter(f => f && (f.q || f.question));
  const [openIdx, setOpenIdx] = useState(null);
  if (!faqs.length) return null;
  return (
    <section style={{
      background: palette.bg, color: palette.text,
      padding: isMobile ? '70px 24px' : '120px 7vw',
    }}>
      <div style={{ maxWidth: 980, margin: '0 auto' }}>
        <div style={{ marginBottom: isMobile ? 36 : 56 }}>
          <div style={{
            fontFamily: FB, fontSize: 10, letterSpacing: '0.28em',
            textTransform: 'uppercase', color: palette.accent, fontWeight: 600,
          }}>{content.eyebrow || content.label || 'Questions'}</div>
          <div style={{ width: 40, height: 1, background: palette.accent, marginTop: 16, marginBottom: 22 }} />
          <h2 style={{
            fontFamily: FD, fontSize: isMobile ? 32 : 44,
            lineHeight: 1.1, letterSpacing: '-0.015em',
            fontWeight: 400, margin: 0,
          }}>{content.headline || content.title || 'Frequently Asked'}</h2>
        </div>
        <div>
          {faqs.map((f, i) => {
            const open = i === openIdx;
            const q = f.q || f.question;
            const a = f.a || f.answer;
            return (
              <div key={i} style={{
                borderBottom: `1px solid rgba(211,154,33,0.22)`,
              }}>
                <button
                  onClick={() => setOpenIdx(o => o === i ? null : i)}
                  style={{
                    width: '100%', background: 'none', border: 'none',
                    padding: '26px 0', textAlign: 'left', cursor: 'pointer',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    color: palette.text, gap: 20,
                  }}
                >
                  <span style={{
                    fontFamily: FD, fontSize: isMobile ? 18 : 22,
                    fontWeight: 400, lineHeight: 1.4,
                  }}>{q}</span>
                  <span style={{
                    color: palette.accent, fontSize: 24, flexShrink: 0,
                    transform: open ? 'rotate(45deg)' : 'rotate(0)',
                    transition: 'transform 280ms ease',
                    lineHeight: 1,
                  }}>+</span>
                </button>
                <div style={{
                  maxHeight: open ? 400 : 0, overflow: 'hidden',
                  transition: 'max-height 400ms ease',
                }}>
                  <p style={{
                    fontFamily: FB, fontSize: isMobile ? 15 : 16,
                    lineHeight: 1.75, fontWeight: 300,
                    color: palette.muted,
                    margin: '0 0 26px', maxWidth: '72ch',
                  }}>{a}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FlagshipImageFull({ content, showcaseHero, listingFirstImage, palette }) {
  const { isMobile } = useBreakpoint();
  const img = resolveImage(content.image, showcaseHero, listingFirstImage);
  if (!img) return null;
  return (
    <section style={{ position: 'relative', background: palette.bg }}>
      <img src={imgUrl(img, { width: 1800 })} alt={content.caption || ''}
           style={{ display: 'block', width: '100%', height: isMobile ? '60vh' : '90vh', objectFit: 'cover' }} />
      {content.caption && (
        <div style={{
          position: 'absolute', left: 0, right: 0, bottom: isMobile ? 28 : 48,
          textAlign: 'center', padding: '0 24px',
        }}>
          <div style={{
            fontFamily: FB, fontSize: 11, letterSpacing: '0.28em',
            textTransform: 'uppercase', color: '#f5f0e8',
            fontWeight: 500, textShadow: '0 2px 18px rgba(0,0,0,0.8)',
          }}>{content.caption}</div>
        </div>
      )}
    </section>
  );
}

function FlagshipRelated({ content, palette }) {
  const { isMobile } = useBreakpoint();
  const items = (content.items || []).filter(Boolean);
  if (!items.length) return null;
  return (
    <section style={{
      background: palette.bg2, color: palette.text,
      padding: isMobile ? '70px 24px' : '120px 7vw',
    }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <h2 style={{
          fontFamily: FD, fontSize: isMobile ? 28 : 36,
          lineHeight: 1.1, fontWeight: 400, margin: '0 0 40px',
          letterSpacing: '-0.015em',
        }}>{content.title || 'You May Also Love'}</h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: isMobile ? 20 : 28,
        }}>
          {items.map((it, i) => (
            <a key={i} href={it.href || '#'} style={{ textDecoration: 'none', color: 'inherit' }}>
              <div style={{
                aspectRatio: '4/5', background: palette.bg3,
                overflow: 'hidden', position: 'relative',
              }}>
                {it.image && (
                  <img src={imgUrl(it.image, { width: 1200 })} alt={it.title || ''}
                       style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                )}
              </div>
              <div style={{ paddingTop: 18 }}>
                {it.location && (
                  <div style={{
                    fontFamily: FB, fontSize: 10, letterSpacing: '0.22em',
                    textTransform: 'uppercase', color: palette.accent, fontWeight: 600,
                  }}>{it.location}</div>
                )}
                <div style={{
                  fontFamily: FD, fontSize: 22,
                  fontWeight: 400, color: palette.text,
                  marginTop: 8, lineHeight: 1.25,
                }}>{it.title}</div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Breadcrumb bar
// ─────────────────────────────────────────────────────────────────────────────
function FlagshipBreadcrumb({ showcase, listing, onBack, onGoDestination, palette, darkMode }) {
  const { isMobile } = useBreakpoint();
  const bg        = darkMode ? 'rgba(14,12,9,0.97)' : 'rgba(250,248,242,0.97)';
  const border    = darkMode ? 'rgba(211,154,33,0.14)' : 'rgba(184,150,46,0.18)';
  const textCol   = darkMode ? '#f5f0e8' : '#1a1a1a';
  const mutedCol  = darkMode ? 'rgba(245,240,232,0.48)' : 'rgba(28,20,16,0.5)';
  const city    = listing?.city    || (typeof showcase?.location === 'object' ? showcase.location?.city    : null) || '';
  const country = listing?.country || (typeof showcase?.location === 'object' ? showcase.location?.country : null) || '';
  const venueName = showcase?.title || showcase?.name || listing?.name || '';
  const listingHref = listing?.slug ? `/${listing.slug}` : null;

  return (
    <div style={{
      background: bg,
      borderBottom: `1px solid ${border}`,
      padding: isMobile ? '0 16px' : '0 36px',
      height: 44,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 12, position: 'relative', zIndex: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, overflow: 'hidden' }}>
        {onBack && (
          <button onClick={onBack} style={{
            fontFamily: FB, fontSize: 11, color: mutedCol,
            background: 'none', border: 'none', cursor: 'pointer',
            padding: 0, fontWeight: 400, letterSpacing: '0.04em',
            transition: 'color 0.2s', flexShrink: 0,
          }}
          onMouseEnter={e => (e.currentTarget.style.color = textCol)}
          onMouseLeave={e => (e.currentTarget.style.color = mutedCol)}
          >← Back</button>
        )}
        {!isMobile && (
          <>
            <span style={{ fontFamily: FB, fontSize: 10, color: mutedCol }}>›</span>
            <button onClick={() => onGoDestination?.('')} style={{
              fontFamily: FB, fontSize: 11, color: mutedCol,
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              letterSpacing: '0.04em',
            }}
            onMouseEnter={e => (e.currentTarget.style.color = textCol)}
            onMouseLeave={e => (e.currentTarget.style.color = mutedCol)}
            >Destinations</button>
          </>
        )}
        {!isMobile && (city || country) && (
          <>
            <span style={{ fontFamily: FB, fontSize: 10, color: mutedCol }}>›</span>
            <span style={{
              fontFamily: FB, fontSize: 11,
              color: mutedCol, letterSpacing: '0.04em',
            }}>{[city, country].filter(Boolean).join(', ')}</span>
          </>
        )}
        {!isMobile && venueName && (
          <>
            <span style={{ fontFamily: FB, fontSize: 10, color: mutedCol }}>›</span>
            <span style={{
              fontFamily: FB, fontSize: 11,
              color: textCol, letterSpacing: '0.04em', fontWeight: 500,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{venueName}</span>
          </>
        )}
      </div>
      {listingHref && (
        <a href={listingHref} style={{
          fontFamily: FB, fontSize: 10,
          letterSpacing: '0.28em', textTransform: 'uppercase',
          color: palette.accent, fontWeight: 600,
          textDecoration: 'none', flexShrink: 0,
          display: 'inline-flex', alignItems: 'center', gap: 8,
        }}>
          View Listing <span>→</span>
        </a>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sticky section nav
// ─────────────────────────────────────────────────────────────────────────────
const NAV_ELIGIBLE = new Set([
  'intro', 'feature', 'mosaic', 'gallery', 'dining',
  'spaces', 'wellness', 'weddings', 'highlight-band', 'films', 'faq',
]);
const NAV_LABEL = {
  intro: 'Overview', feature: 'Highlights', mosaic: 'Moments',
  gallery: 'Gallery', films: 'Films', dining: 'Dining',
  spaces: 'Spaces', wellness: 'Wellness', weddings: 'Weddings',
  'highlight-band': 'Story', faq: 'FAQs',
};

function FlagshipSectionNav({ sections, palette, onVisibleChange }) {
  const { isMobile } = useBreakpoint();
  const [visible, setVisible] = useState(false);
  const [activeId, setActiveId] = useState(null);

  const navSections = sections.filter(s => NAV_ELIGIBLE.has(s.type));

  useEffect(() => {
    const onScroll = () => {
      const show = window.scrollY > (window.innerHeight * 0.7);
      setVisible(show);
      onVisibleChange?.(show);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [onVisibleChange]);

  useEffect(() => {
    if (!visible) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) setActiveId(e.target.id); });
    }, { rootMargin: '-45% 0px -45% 0px' });
    navSections.forEach(s => {
      const el = document.getElementById(`sec-${s.id}`);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [visible, navSections.length]);

  if (isMobile || !navSections.length) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 700,
      background: 'rgba(10,8,6,0.86)',
      backdropFilter: 'blur(18px)',
      WebkitBackdropFilter: 'blur(18px)',
      borderBottom: `1px solid rgba(211,154,33,0.16)`,
      transform: visible ? 'translateY(0)' : 'translateY(-100%)',
      transition: 'transform 360ms cubic-bezier(0.19, 1, 0.22, 1)',
      height: 56,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <nav style={{ display: 'flex', gap: 28 }}>
        {navSections.map(s => {
          const active = `sec-${s.id}` === activeId;
          return (
            <a
              key={s.id}
              href={`#sec-${s.id}`}
              data-active={active}
              className="flagship-sec-nav-link"
              style={{
                fontFamily: FB, fontSize: 10,
                letterSpacing: '0.22em', textTransform: 'uppercase',
                color: active ? palette.accent : 'rgba(245,240,232,0.55)',
                textDecoration: 'none', fontWeight: 600,
                transition: 'color 220ms ease',
              }}
            >
              {NAV_LABEL[s.type] || s.type}
            </a>
          );
        })}
      </nav>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main renderer
// ─────────────────────────────────────────────────────────────────────────────
export default function ShowcaseRendererFlagship({
  sections = [],
  showcase = {},
  listing = null,
  listingFirstImage = null,
  isPreview = false,
  interactive = false,
  selectedId = null,
  onSelectSection = null,
  darkMode = false,
  onToggleDark = null,
  onNavigateStandard = null,
  onGoDestination = null,
  onBack = null,
}) {
  const heroUrl = showcase.hero_image_url || null;
  const venueTitle = showcase?.title || showcase?.name || listing?.name || '';

  const palette = useMemo(() => {
    if (isPreview) return buildPalette(showcase.theme);
    const base = darkMode ? DARK_PALETTE : LIGHT_DEFAULTS;
    let t = showcase.theme;
    if (typeof t === 'string') { try { t = JSON.parse(t); } catch (_) { t = null; } }
    if (!t || !t.accent) return base;
    const accent = darkMode ? (t.darkAccent || t.accent) : t.accent;
    return {
      ...base, accent, label: accent, navActive: accent,
      ...(t.bg  && !darkMode && { bg:  t.bg  }),
      ...(t.bg2 && !darkMode && { bg2: t.bg2 }),
      ...(t.bg3 && !darkMode && { bg3: t.bg3 }),
    };
  }, [isPreview, darkMode, showcase.theme]);

  const [sectionNavVisible, setSectionNavVisible] = useState(false);

  function renderSection(section) {
    const { type, content = {}, layout = {} } = section;
    const shared = { showcaseHero: heroUrl, listingFirstImage, palette };

    let inner = null;
    switch (type) {
      case 'hero':
        inner = <FlagshipHero content={content} venueTitle={venueTitle} {...shared} />;
        break;
      case 'intro':
        inner = <FlagshipIntro content={content} palette={palette} />;
        break;
      case 'rooms':
        inner = <FlagshipRooms content={content} {...shared} />;
        break;
      case 'gallery':
        inner = <FlagshipGallery content={content} palette={palette} />;
        break;
      case 'cta':
        inner = <FlagshipCta content={content} venueName={venueTitle} {...shared} />;
        break;
      case 'stats':
        inner = <FlagshipStats content={content} palette={palette} />;
        break;
      case 'quote':
        inner = <FlagshipQuote content={content} {...shared} />;
        break;
      case 'highlight-band':
        inner = <FlagshipHighlightBand content={content} palette={palette} />;
        break;
      case 'feature':
        inner = <FlagshipFeature content={content} {...shared} heightVh={52} />;
        break;
      case 'mosaic':
        inner = <FlagshipMosaic content={content} {...shared} />;
        break;
      case 'dining':
      case 'spaces':
      case 'wellness':
      case 'weddings':
        inner = <FlagshipFeature content={content} {...shared} heightVh={60} />;
        break;
      case 'verified':
        inner = <FlagshipVerified content={content} palette={palette} />;
        break;
      case 'nearby':
        inner = <FlagshipNearby content={content} palette={palette} />;
        break;
      case 'map':
        inner = <FlagshipMap
                  content={content} palette={palette}
                  showcaseName={showcase?.title}
                  showcaseLocation={typeof showcase?.location === 'string' ? showcase.location : ''}
                />;
        break;
      case 'films':
        inner = <FlagshipFilms content={content} palette={palette} />;
        break;
      case 'bento-grid':
        inner = <FlagshipBentoGrid content={content} palette={palette} />;
        break;
      case 'pricing':
        inner = <FlagshipPricing content={content} palette={palette} />;
        break;
      case 'faq':
        inner = <FlagshipFaq content={content} palette={palette} />;
        break;
      case 'image-full':
        inner = <FlagshipImageFull content={content} {...shared} />;
        break;
      case 'related':
        inner = content.items?.length
          ? <FlagshipRelated content={content} palette={palette} />
          : null;
        break;
      default:
        inner = null;
    }

    if (!inner) return null;

    const sectionContent = (
      <div
        id={`sec-${section.id}`}
        data-showcase-section={type}
        style={{ scrollMarginTop: (isPreview || interactive) ? 0 : 108 }}
      >
        {inner}
      </div>
    );

    if (interactive && onSelectSection) {
      return (
        <div
          key={section.id}
          onClick={(e) => { e.stopPropagation(); onSelectSection(section.id); }}
          style={{
            cursor: 'pointer',
            outline: section.id === selectedId ? `2px solid ${palette.accent}` : 'none',
            outlineOffset: -2,
          }}
        >{sectionContent}</div>
      );
    }
    return <div key={section.id}>{sectionContent}</div>;
  }

  return (
    <div style={{ background: palette.bg, color: palette.text, minHeight: '100vh' }}>
      <FlagshipStyles />
      {!isPreview && (
        <>
          <div style={{ background: darkMode ? 'rgba(11,9,6,0.97)' : '#f2f0ea' }}>
            <div style={{
              position: 'fixed', top: 0, left: 0, right: 0, zIndex: 699,
              transform:  sectionNavVisible ? 'translateY(-110%)' : 'translateY(0)',
              transition: 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
            }}>
              <HomeNav
                hasHero={false}
                darkMode={darkMode}
                onToggleDark={onToggleDark}
                onNavigateStandard={onNavigateStandard}
              />
            </div>
            <div style={{ height: 61 }} />
            <FlagshipBreadcrumb
              showcase={showcase}
              listing={listing}
              onBack={onBack}
              onGoDestination={onGoDestination}
              palette={palette}
              darkMode={darkMode}
            />
          </div>
          <FlagshipSectionNav
            sections={sections}
            palette={palette}
            onVisibleChange={setSectionNavVisible}
          />
        </>
      )}
      {sections.map(section => renderSection(section))}
    </div>
  );
}
